import React, { useState, useEffect } from 'react';
import { CommonCard } from '@/components/common/CommonCard';
import Button from '@/components/common/Button';
import FormField from '@/components/common/FormField';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import { useForm, Controller } from 'react-hook-form';
import { errorTypes } from '@/utility/validation';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '@/redux/store';
import { fetchHolidaySettings } from '@/redux/features/UserSlice';

export interface ICheckoutMessageForm {
    message: string;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    status: 'active' | 'inactive';
}

const CheckoutMessage = () => {
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();
    const { holidaySettings, isHolidayLoading, user } = useSelector((state: RootState) => state.user)
    const {
        register,
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<ICheckoutMessageForm>({
        defaultValues: {
            status: 'active',
            message: '',
            startDate: '',
            startTime: '',
            endDate: '',
            endTime: ''
        }
    });

    useEffect(() => {
        if (holidaySettings) {
            reset(holidaySettings);
        } else {
            reset({
                message: '',
                startDate: '',
                startTime: '',
                endDate: '',
                endTime: '',
                status: 'inactive'
            });
        }
    }, [reset]);

    const onSubmit = async (data: ICheckoutMessageForm) => {
        setLoading(true);
        try {
            const payload = {
                // id: user?.id,
                message: data.message,
                status: data.status === 'active' ? 'active' : 'inactive',
                startDate: data.startDate,
                startTime: data.startTime,
                endDate: data.endDate,
                endTime: data.endTime,
            };

            await callApi(`${API_URL}${API_ENDPOINT.checkoutMessage}`, {
                method: 'POST',
                data: payload
            });
            dispatch(fetchHolidaySettings());
            showToastNotification('success', 'Checkout message saved successfully');
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    if (isHolidayLoading) {
        return (
            <div className="flex justify-center items-center py-12 bg-card border rounded-xl">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <CommonCard title="Checkout Message" >
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Message Textarea */}
                    <FormField
                        label="Message"
                        name="message"
                        type="textarea"
                        rows={4}
                        placeholder="Type your checkout message here..."
                        error={errors.message?.message}
                        {...register('message', { ...errorTypes.required('Message') })}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        {/* Start Date & Time */}
                        <FormField
                            label="Start Date"
                            name="startDate"
                            type="date"
                            error={errors.startDate?.message}
                            {...register('startDate', { ...errorTypes.required('Start Date') })}
                        />
                        <FormField
                            label="Start Time"
                            name="startTime"
                            type="time"
                            error={errors.startTime?.message}
                            {...register('startTime', { ...errorTypes.required('Start Time') })}
                        />

                        {/* End Date & Time */}
                        <FormField
                            label="End Date"
                            name="endDate"
                            type="date"
                            error={errors.endDate?.message}
                            {...register('endDate', { ...errorTypes.required('End Date') })}
                        />
                        <FormField
                            label="End Time"
                            name="endTime"
                            type="time"
                            error={errors.endTime?.message}
                            {...register('endTime', { ...errorTypes.required('End Time') })}
                        />
                    </div>

                    {/* Active Status Toggle */}
                    <div className="flex items-center space-x-3 pt-2">
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <Switch
                                    id="status"
                                    checked={field.value === 'active'}
                                    onCheckedChange={(checked) => field.onChange(checked ? 'active' : 'inactive')}
                                />
                            )}
                        />
                        <Label htmlFor="status" className="text-sm font-medium cursor-pointer">
                            Active
                        </Label>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end pt-4">
                        <Button
                            type="submit"
                            isLoading={loading}
                            className="px-10 h-11 bg-[#901B1B] hover:bg-[#7a1616] text-white rounded-md transition-colors"
                        >
                            Save Checkout Message
                        </Button>
                    </div>
                </form>
            </CommonCard>
        </div>
    );
};

export default CheckoutMessage;
